<script setup>
const props = defineProps({
    // The bridge (app/plugins/modal-route.client.js) passes this through as a
    // prop when it opens the login modal for a deep link (e.g. braindate
    // redirecting an unauthenticated visitor to /login?action=braindate).
    action: {
        type: String,
        default: null,
    },
});

const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

const route = useRoute();

// Prefer the explicit prop (deep-link bridge); fall back to the query string
// so direct query-string navigation still works.
const currentAction = computed(() => props.action ?? route.query.action ?? null);

const copy = computed(() => {
    if (currentAction.value === 'favorite') {
        return 'In order to favorite a session, You must log in using the email you registered with and your ticket confirmation number.';
    }
    if (currentAction.value === 'reservations') {
        return 'In order to reserve a seat for a session, You must log in using the email you registered with and your ticket confirmation number.';
    }
    if (currentAction.value === 'braindate') {
        // Says "redirected" again because it is (UNB-693): the hand-off went back
        // to an automatic same-tab redirect, so promising a new tab here would
        // describe something that no longer happens.
        return 'Please login with the email you used to register for UNBOUND and your ticket number. You’ll be logged into UNBOUND and then redirected to Braindate.';
    }
    return 'In order to add sessions or events to My Schedule, use the schedule builder, save a seat, or add activities to your favorites, you must login using the email you registered with and your ticket number.';
});

const headline = computed(() => {
    if (currentAction.value === 'braindate') {
        return 'You must be logged into UNBOUND to use Braindate';
    }
    return 'Login';
});
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        :headline="headline"
        headline-class="login-modal__headline"
        inner-class="login-modal__inner"
        @close="emit('close')"
    >
        <FragmentsAuthForm v-model:open="open" :copy="copy" />
    </FragmentsModalShell>
</template>

<style>
.login-modal__inner {
    gap: 30px;
    padding-top: 25px;
    padding-bottom: 60px;
    border-radius: 10px;
}

/* Not on mobile. The shell's body already pads 90px there to clear the pinned
   ✕ and the blur strip beneath it, so this 25px stacked on top and pushed the
   headline to 115px — the "content sits too low" Nick reported, which he noted
   was worst here (2026-08-19). Removing it lands the headline at 90px, exactly
   the blur strip's lower edge, which is what that 90px is for. Desktop keeps
   the 25px: there the panel is centred and there is no strip to clear. */
@media (max-width: 639px) {
    .login-modal__inner {
        padding-top: 0;
    }
}

.login-modal__headline {
    font-family: var(--font-hubspot-serif);
    font-weight: 400;
    font-size: 25px;
    line-height: 1.2;
    margin-bottom: 0;
}

/* 36px is the desktop size. Mobile takes the shared 25px headline (Nick's
   mobile type mockup, Figma 5999:149541) — this modal's was the largest on the
   site and the one he named. */
@media (min-width: 640px) {
    .login-modal__headline {
        font-size: 36px;
    }
}
</style>
